import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import Typography from '@mui/material/Typography';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import { usePersonNameOptions } from '../features/users/usePersonNameOptions';
import { useUserDirectory } from '../features/users/userDirectory';
import { distinctPeople } from '@ast-data/shared';
import type { EmploymentWindow } from '../features/users/employment';
import { renderCheckableOption } from './checkableOption';
import { joinedToChips } from './formFields/multiSelectField';

// ---------------------------------------------------------------------------
/**
 * The person picker's shared behaviour — one place, so a Fotograf is picked the
 * way an Ausgräber is, in the form and in the grid cell alike.
 *
 * A person field is a PICKER, not a text field with suggestions: a name typed
 * beside an account is a second spelling of somebody, and nothing later can tell
 * the two apart. Typing filters the list; leaving the field puts back what was
 * selected.
 *
 * Two things are added to the list, and neither loosens it: what the record
 * already says (`personPickerOptions`), and — for whoever holds `nameFreely` —
 * an explicit "take it as free text" entry.
 */
// ---------------------------------------------------------------------------

/**
 * Marks the synthetic free-text entry. U+0000 cannot be typed into a browser
 * input, so the marker can never collide with a name somebody actually wrote.
 */
const FREE_TEXT_PREFIX = '\u0000free:';

const filterNames = createFilterOptions<string>();

/** The name behind a picked option — the synthetic entry unwrapped. */
export function personName(option: string | null | undefined): string {
    if (!option) {
        return '';
    }
    return option.startsWith(FREE_TEXT_PREFIX)
        ? option.slice(FREE_TEXT_PREFIX.length)
        : option;
}

/** Several picked options as the plain names to store. */
export function personNames(options: readonly string[]): string[] {
    return options.map((option) => personName(option));
}

/**
 * What every person field shares: the accounts to offer and the Autocomplete
 * behaviour around them. Held ONCE by the form or the grid (it is a hook).
 */
export interface PersonPickerConfig {
    /**
     * Display names of the accounts this record may pick, sorted: the active
     * ones, narrowed to the record's day when the form gave one.
     */
    accounts: string[];
    /**
     * Every active account's name, whatever the day. What splits a ', '-joined
     * field back into people: a name with a comma in it ("Hecht, Katrin") is
     * recognised by being an account, and that must not depend on whether the
     * person was employed on THIS record's day.
     */
    knownNames: string[];
    /** May this user commit a name that is not on the list? */
    freeText: boolean;
    /** Of those accounts, the ones markable as Grabungsleiter. */
    excavationLeads: ReadonlySet<string>;
    /**
     * Whether a name will be linked to an account on save — by the same rule
     * the save uses. Linked people come shown by their account's current name,
     * so that name resolves. True while the accounts are still loading, so no
     * chip flashes a warning it then takes back.
     */
    isLinked: (name: string) => boolean;
    /**
     * The names with nobody in them twice — the same account, or names that
     * read alike — first mention kept. The rule the server stores lists by
     * (`distinctPeople`), applied as the field changes, so a paste or a second
     * spelling does not even show twice.
     */
    distinct: (names: readonly string[]) => string[];
    /**
     * The two headings a Grabungsleiter field groups under, already TRANSLATED:
     * MUI prints what `groupBy` returns, so a message id would appear on screen.
     */
    groupLabels: { lead: string; other: string };
    /** Everything but the options and the option row — see personPickerProps. */
    shared: PersonPickerSharedProps;
    /** The option row, ticked (multi) or not (single). */
    renderOption: (ticks: boolean) => PersonPickerRenderOption;
}

/** What every person field spreads onto its Autocomplete, whatever its shape. */
export interface PersonPickerSharedProps {
    autoHighlight: true;
    freeSolo: boolean;
    autoSelect: false;
    clearOnBlur: true;
    selectOnFocus: true;
    handleHomeEndKeys: true;
    filterOptions: (
        candidates: string[],
        state: {
            inputValue: string;
            getOptionLabel: (option: string) => string;
        }
    ) => string[];
    getOptionLabel: (option: string) => string;
}

export type PersonPickerRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement> & { key: unknown },
    option: string,
    state: AutocompleteRenderOptionState
) => React.ReactNode;

/**
 * The person fields' configuration, held once by a form. Given the record's
 * day or span, the accounts are only those employed during it — every person
 * field of the form, and its F2 list, then offers just them.
 */
export function usePersonPicker(window?: EmploymentWindow): PersonPickerConfig {
    const intl = useIntl();
    const {
        options: accounts,
        freeText,
        excavationLeads
    } = usePersonNameOptions(window);
    const { options: knownNames } = usePersonNameOptions();
    const { resolver, ready } = useUserDirectory();

    return useMemo(() => {
        const shared = {
            /**
             * Fifty-odd accounts are typed at, not scrolled through: the first
             * match is highlighted, so Enter takes it. The free-text entry sits
             * last, so Enter prefers a real account.
             */
            autoHighlight: true as const,
            /**
             * ONLY for whoever may name freely — this is the whole gate, and
             * the reason MUI's Creatable pattern needs it: without `freeSolo`
             * a value that is not an option is refused, so the synthetic entry
             * could be clicked but never picked with the keyboard.
             */
            freeSolo: freeText,
            /**
             * Typing alone still commits NOTHING: `autoSelect` is what would
             * take the input on blur, and `clearOnBlur` puts the selected value
             * back when the field is left half-typed.
             */
            autoSelect: false as const,
            clearOnBlur: true as const,
            selectOnFocus: true as const,
            handleHomeEndKeys: true as const,
            filterOptions: (
                candidates: string[],
                state: {
                    inputValue: string;
                    getOptionLabel: (option: string) => string;
                }
            ) => {
                const filtered = filterNames(candidates, state);
                const typed = state.inputValue.trim();
                const known = candidates.some(
                    (option) => option.toLowerCase() === typed.toLowerCase()
                );
                if (freeText && typed && !known) {
                    filtered.push(`${FREE_TEXT_PREFIX}${typed}`);
                }
                return filtered;
            },
            getOptionLabel: (option: string) => personName(option)
        };

        // Ticks and alignment come from renderCheckableOption — the free-text
        // entry is an ACTION, so it gets the spacer rather than a box.
        const row = {
            tickable: (option: string) => !option.startsWith(FREE_TEXT_PREFIX),
            label: (option: string) =>
                option.startsWith(FREE_TEXT_PREFIX) ? (
                    <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                    >
                        {intl.formatMessage(
                            { id: 'person.freeText.add' },
                            { name: personName(option) }
                        )}
                    </Typography>
                ) : (
                    option
                )
        };

        return {
            accounts,
            knownNames,
            freeText,
            excavationLeads,
            isLinked: (name: string) => !ready || resolver(name) !== null,
            distinct: (names: readonly string[]) =>
                distinctPeople(
                    names,
                    names.map((name) => resolver(name)?.id ?? '')
                ).names,
            groupLabels: {
                lead: intl.formatMessage({
                    id: 'person.group.excavationLead'
                }),
                other: intl.formatMessage({ id: 'person.group.other' })
            },
            shared,
            renderOption: (ticks: boolean) =>
                renderCheckableOption({ ...row, ticks })
        };
    }, [
        accounts,
        knownNames,
        freeText,
        excavationLeads,
        resolver,
        ready,
        intl
    ]);
}

/**
 * The F2 list of "the value from a moment ago", filtered by the same rule the
 * picker itself applies.
 *
 * Those values are the last ones this field carried ON OTHER RECORDS, and the
 * menu writes what is picked straight into the field. Unfiltered it is the very
 * route this picker exists to close: one misspelling, offered to every record
 * after it. So an entry survives only if the field could have been picked that
 * way — every one of its names an account, or what THIS record already says.
 *
 * Whoever may name freely keeps the whole list: they may type the name anyway.
 */
export function pickableRecent(
    config: PersonPickerConfig,
    recent: readonly string[] | undefined,
    current: string | readonly string[]
): string[] {
    const entries = recent ?? [];
    if (config.freeText) {
        return [...entries];
    }
    const pickable = new Set(personPickerOptions(config, current));
    return entries.filter((entry) => {
        const names = joinedToChips(entry, config.knownNames);
        return names.length > 0 && names.every((name) => pickable.has(name));
    });
}

/**
 * The picker with `first` offered as a group of their own, ahead of everyone
 * else, under the given headings — the grouping a Grabungsleiter field uses,
 * for another kind of "these first" (who worked on the Maßnahme). Spread with
 * `leads: true`; everyone stays offered.
 */
export function withFirstGroup(
    config: PersonPickerConfig,
    first: ReadonlySet<string>,
    labels: { first: string; other: string }
): PersonPickerConfig {
    return {
        ...config,
        excavationLeads: first,
        groupLabels: { lead: labels.first, other: labels.other }
    };
}

/** The names one field may hold: the accounts plus what this record says. */
function personPickerOptions(
    config: PersonPickerConfig,
    current: string | readonly string[]
): string[] {
    const currentNames = (typeof current === 'string' ? [current] : current)
        .map((name) => `${name ?? ''}`.trim())
        .filter(Boolean);
    return [...new Set([...config.accounts, ...currentNames])];
}

/** How ONE person field differs from the next. */
export interface PersonFieldShape {
    /** What the record says right now — one name, or several. */
    current: string | readonly string[];
    /** Several people rather than one: ticked options and chips. */
    multiple?: boolean;
    /**
     * A Grabungsleiter field. The accounts marked as possible leads are offered
     * as a group of their own, ahead of everyone else. Everyone stays offered;
     * only the order and the headings change.
     */
    leads?: boolean;
}

/**
 * Everything one person field spreads onto its Autocomplete: the options it
 * offers, the option row, and the grouping.
 *
 * The options are the accounts plus whatever that record ALREADY says — a
 * deactivated account, or a name that never was one, stays pickable until it is
 * replaced. MUI also needs the value to BE an option, or it warns and drops it.
 */
export function personPickerProps(
    config: PersonPickerConfig,
    { current, multiple = false, leads = false }: PersonFieldShape
) {
    const options = personPickerOptions(config, current);
    const isLead = (name: string) => config.excavationLeads.has(name);
    return {
        ...config.shared,
        renderOption: config.renderOption(multiple),
        /**
         * MUI draws a heading wherever the group CHANGES down the list, so a
         * list not sorted by group repeats its headings. Hence the reorder,
         * not a `groupBy` on its own.
         */
        options: leads
            ? [
                  ...options.filter(isLead),
                  ...options.filter((name) => !isLead(name))
              ]
            : options,
        // Undefined on an ordinary person field: MUI reads that as ungrouped.
        groupBy: leads
            ? (option: string) =>
                  isLead(option)
                      ? config.groupLabels.lead
                      : config.groupLabels.other
            : undefined
    };
}
